/* eslint-disable @typescript-eslint/no-magic-numbers -- the rule was disabled before a comment became required */
import { type FC, useEffect, useRef } from 'react'

import { useExperimentalAddStaticFileHost } from '../../../../experiments/new-cdn-experiment/useExperimentalAddStaticFileHost'
import { useGlobalConfigContext } from '../../../../global-config'
import {
  IMAGE_PRODUCT_FALLBACK_SRC,
  IMAGE_PRODUCT_PRESCRIPTION_FALLBACK_SRC,
  imageAccessorIsImageHostedOnCdn,
  NextImageWithFallback,
  Picture,
} from '../../../../image/'
import { productAccessorIsPrescriptionProduct } from '../../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { useProductPageContext } from '../../productPageContext'

import { type ProductCarouselThumbnailProps } from './ProductCarouselThumbnailProps'

const IMAGE_SIZES_RELATIVE_TO_VIEWPORT_WIDTH: [number, number][] = [
  [1000, 128],
  [770, 64],
]
const SMALLEST_IMAGE_SIZE = 48

const ProductCarouselThumbnail: FC<ProductCarouselThumbnailProps> = ({
  alt: altProp,
  currentImageIndex,
  image,
  index,
  onClick,
}) => {
  const ref = useRef<HTMLButtonElement | null>(null)
  const { product } = useProductPageContext()
  const { cdn } = useGlobalConfigContext()
  const isCdnImage = imageAccessorIsImageHostedOnCdn(image, cdn.url)
  const { addStaticCdnHostToUrl } = useExperimentalAddStaticFileHost()
  const fallbackImageUrl = productAccessorIsPrescriptionProduct(product)
    ? addStaticCdnHostToUrl(IMAGE_PRODUCT_PRESCRIPTION_FALLBACK_SRC)
    : addStaticCdnHostToUrl(IMAGE_PRODUCT_FALLBACK_SRC)

  const alt = altProp ?? product.name

  /*
   * Quick fix to avoid scroll into view on initial render.
   * Hope this could be removed after refactoring or ful rewrite as mentioned in
   * ProductCarousel component
   */
  const skipInitialScroll = useRef(true)

  useEffect(() => {
    if (currentImageIndex === index && !skipInitialScroll.current) {
      ref.current?.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'nearest',
      })
    }

    if (currentImageIndex !== index) {
      skipInitialScroll.current = false
    }
  }, [currentImageIndex, index])

  return (
    <button
      className="w-1/3 flex-none px-2"
      data-qa-id={`product-thumbnail-${index}`}
      onClick={(): void => {
        onClick(index)
      }}
      ref={ref}
      type="button"
    >
      <div className="relative overflow-hidden rounded-lg border border-dark-primary-strong p-2.5 before:block before:pt-[100%] hover:border-dark-brand">
        {isCdnImage ? (
          <Picture
            alt={alt}
            className="absolute inset-2"
            imageSizes={IMAGE_SIZES_RELATIVE_TO_VIEWPORT_WIDTH}
            lazy={index > 0}
            smallestImageSize={SMALLEST_IMAGE_SIZE}
            src={image}
          />
        ) : (
          <NextImageWithFallback
            alt={alt}
            className="object-contain"
            fallbackSrc={fallbackImageUrl}
            fill
            loading={index > 0 ? 'lazy' : 'eager'}
            src={image}
            unoptimized
          />
        )}
      </div>
    </button>
  )
}

export { ProductCarouselThumbnail }
